import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, Sparkles, ArrowRight, Loader2, ChevronRight, RotateCcw, Check } from "lucide-react";
import api from "../lib/api";

/**
 * "Today's lesson" card surfaced at the top of Dashboard.
 *
 * Shows:
 *  - The current daily streak (Flame icon)
 *  - The next module to study (least-completed)
 *  - 1 deck card the user can flip → mark done, advancing progress
 *  - Progress bar for that module
 *  - "See all lessons" link to the Learn hub
 */
export default function TodaysLessonCard() {
  const [loading, setLoading] = useState(true);
  const [deck, setDeck] = useState(null);
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchToday = async () => {
    setLoading(true);
    try {
      const r = await api.get("/lessons/today?limit=5");
      setDeck(r.data);
      setIdx(0);
      setFlipped(false);
    } catch (e) {
      console.warn("today's lesson load failed", e);
      setDeck(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchToday();
  }, []);

  if (loading) {
    return (
      <div
        className="rounded-2xl bg-white dark:bg-[#1F2421] border border-[#EAE6E1] dark:border-[#2C302E] p-6 flex items-center gap-3 text-sm text-[#6B7068]"
        data-testid="todays-lesson-loading"
      >
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading today's lesson…
      </div>
    );
  }

  if (!deck || !deck.module || !deck.cards || deck.cards.length === 0) {
    return null; // Don't break the dashboard if lessons aren't seeded yet
  }

  const card = deck.cards[idx];
  const total = deck.module_progress?.total || 0;
  const completed = deck.module_progress?.seen_card_ids?.length || 0;
  const pct = deck.module_progress?.percent ?? 0;
  const streak = deck.streak?.streak ?? 0;

  const advance = async () => {
    if (!card) return;
    setSubmitting(true);
    try {
      const r = await api.post("/lessons/progress", {
        module_slug: deck.module.slug,
        card_id: card.id,
        correct: true,
      });
      // Optimistically update progress + streak
      setDeck((d) => d && {
        ...d,
        module_progress: r.data.module_progress,
        streak: r.data.streak,
      });
      const next = idx + 1;
      if (next < deck.cards.length) {
        setIdx(next);
        setFlipped(false);
      } else {
        // Refresh — picks the next least-completed module
        await fetchToday();
      }
    } catch (e) {
      console.warn("progress mark failed", e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      className="rounded-2xl bg-white dark:bg-[#1F2421] border border-[#EAE6E1] dark:border-[#2C302E] p-6 md:p-8 grid md:grid-cols-[1fr_320px] gap-6 md:gap-10"
      data-testid="todays-lesson-card"
    >
      <div className="flex flex-col">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-3.5 h-3.5 text-[#D18E63]" strokeWidth={2} />
          <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#D18E63]">
            Today's lesson
          </span>
        </div>
        <h3 className="font-heading text-2xl md:text-3xl tracking-tighter text-[#2A2F2B] dark:text-[#E8E6E2] mt-0.5">
          {deck.module.title}
        </h3>
        <p className="text-sm text-[#6B7068] mt-1">{deck.module.summary}</p>

        <div className="mt-4 flex items-center justify-between text-xs">
          <span className="font-mono-ticker text-[#6B7068]">
            {completed} of {total} cards · {pct}% completed
          </span>
          <span
            className="inline-flex items-center gap-1 text-[#D18E63] font-semibold"
            data-testid="lesson-streak-badge"
            title={`${streak}-day streak`}
          >
            <Flame className="w-3.5 h-3.5" strokeWidth={2} />
            {streak} day{streak === 1 ? "" : "s"}
          </span>
        </div>
        <div
          className="mt-2 h-1.5 rounded-full bg-[#EAE6E1] dark:bg-[#2C302E] overflow-hidden"
          aria-label="Module progress"
          data-testid="lesson-progress-bar"
        >
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${pct}%`, backgroundColor: deck.module.color || "#547A8F" }}
          />
        </div>

        <div className="mt-6 flex items-center gap-3">
          <Link
            to="/learn"
            data-testid="lesson-see-all-link"
            className="text-sm text-[#547A8F] dark:text-[#87A8BA] hover:underline inline-flex items-center gap-1"
          >
            See all modules <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>

      <div className="flex flex-col">
        <button
          type="button"
          onClick={() => setFlipped((v) => !v)}
          data-testid="lesson-card-flip"
          className="text-left relative rounded-2xl bg-[#F9F8F6] dark:bg-[#252A27] border border-[#EAE6E1] dark:border-[#2C302E] p-5 min-h-[180px] flex flex-col justify-between transition-soft hover:shadow-md"
          aria-label={flipped ? "Show question" : "Show answer"}
        >
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#6B7068] mb-2">
              {flipped ? "Answer" : "Question"} · card {idx + 1} of {deck.cards.length}
            </div>
            <p className="text-[#2A2F2B] dark:text-[#E8E6E2] text-base leading-relaxed">
              {flipped ? card.a : card.q}
            </p>
          </div>
          <div className="mt-3 inline-flex items-center gap-1 text-xs text-[#6B7068]">
            <RotateCcw className="w-3 h-3" /> Tap to flip
          </div>
        </button>

        <button
          type="button"
          onClick={advance}
          disabled={submitting}
          data-testid="lesson-card-got-it"
          className="mt-3 inline-flex items-center justify-center gap-1.5 py-3 rounded-full bg-[#2A2F2B] hover:bg-[#547A8F] text-white text-sm font-medium transition-soft disabled:opacity-60"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" strokeWidth={2.25} />}
          Got it — next card
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </section>
  );
}
